import {
  DEFAULT_QUERY_OPTIONS,
  useCreateOne,
  useDeleteOne,
  useGetList,
  useGetOne,
  useMutationWrapper,
} from "#hooks/adapters/adapterUtils";
import type { UseQueryOptions } from "@tanstack/react-query";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import {
  Resources,
  WorkerAdapter,
  WorkerAdapterQueryKeys,
  WorkerRunAdapter,
  WorkerRunAdapterQueryKeys,
  WorkerRunReportSchema,
  type WorkerRunReportType,
  type WorkerType,
} from "@validereinc/domain";
import { useMemo } from "react";

export const useListWorkers = useGetList(
  WorkerAdapter.getList,
  Resources.WORKER
);

export const useGetOneWorker = useGetOne(
  WorkerAdapter.getOne,
  Resources.WORKER
);

/**
 * Fetches multiple workers by id in a single batch (one request per id).
 * Use when you have a list of distinct worker ids and need worker details for all of them.
 */
export const useGetManyWorkers = (ids: string[]) =>
  useQueries<
    Array<
      UseQueryOptions<
        Awaited<ReturnType<typeof WorkerAdapter.getOne>>,
        unknown,
        WorkerType | undefined
      >
    >
  >({
    queries: ids.map((id) => ({
      queryKey: WorkerAdapterQueryKeys.getOne({ id }),
      queryFn: () => WorkerAdapter.getOne({ id }),
      select: (resp) => resp?.data,
      enabled: Boolean(id),
      ...DEFAULT_QUERY_OPTIONS,
    })),
  });

export const useCreateOneWorker = useCreateOne(
  WorkerAdapter.createOne,
  Resources.WORKER
);

export const useDeleteOneWorker = useDeleteOne(
  WorkerAdapter.deleteOne,
  Resources.WORKER
);

export const useListWorkerRuns = useGetList(
  WorkerRunAdapter.getList,
  Resources.WORKER_RUN
);

export const useGetOneWorkerRun = useGetOne(
  WorkerRunAdapter.getOne,
  Resources.WORKER_RUN
);

export const useListWorkerRunReports = (
  params: Parameters<typeof WorkerRunAdapter.reports.getList>[0],
  options: { enabled?: boolean } = {}
) =>
  useQuery<
    Awaited<ReturnType<typeof WorkerRunAdapter.reports.getList>>,
    unknown,
    WorkerRunReportType[]
  >({
    queryKey: WorkerRunAdapterQueryKeys.reports.getList(params),
    queryFn: async () =>
      WorkerRunReportSchema.array().parse(
        await WorkerRunAdapter.reports.getList(params)
      ),
    enabled: Boolean(params.id) && options.enabled !== false,
    ...DEFAULT_QUERY_OPTIONS,
  });

export const useCreateOneWorkerRun = useCreateOne(
  WorkerRunAdapter.createOne,
  Resources.WORKER_RUN
);

export const useListWorkerVersions = useGetList(
  WorkerAdapter.versions.getList,
  Resources.WORKER,
  "versions"
);

/**
 * Fetches a single worker version by worker id and version number.
 */
export const useGetOneWorkerVersion = (
  params: { workerId: string; version: number },
  options: { enabled?: boolean } = {}
) => {
  const queryParams = {
    id: params.workerId,
    meta: { id: params.workerId, version: params.version },
  };
  return useQuery({
    queryKey: WorkerAdapterQueryKeys.versions.getOne(queryParams),
    queryFn: () => WorkerAdapter.versions.getOne(queryParams),
    select: (resp) => resp?.data,
    enabled:
      Boolean(params.workerId) &&
      params.version != null &&
      options.enabled !== false,
    ...DEFAULT_QUERY_OPTIONS,
  });
};

export const useCreateOneWorkerVersion = useCreateOne(
  WorkerAdapter.versions.createOne,
  Resources.WORKER,
  "versions"
);

export const useUpdateOneWorkerVersion = (options = {}) =>
  useMutationWrapper(
    WorkerAdapter.versions.updateOne,
    [Resources.WORKER, "versions"],
    {
      successMessage: "Successfully updated worker version",
      errorMessage: "Failed to update worker version",
      ...options,
    }
  );

export const useInfiniteListWorkerVersions = (
  {
    workerId,
    filters = {},
    pageSize = 25,
  }: {
    workerId: string;
    filters?: Parameters<typeof WorkerAdapter.versions.getList>[0]["filters"];
    pageSize?: number;
  },
  options: { enabled?: boolean } = {}
) =>
  useInfiniteQuery({
    queryKey: [
      ...WorkerAdapterQueryKeys.versions.getList({
        meta: { id: workerId },
        filters,
      }),
      "infinite",
      pageSize,
    ],
    queryFn: ({ pageParam }) =>
      WorkerAdapter.versions.getList({
        meta: { id: workerId },
        page: pageParam ?? 1,
        pageSize,
        sortBy: "version",
        sortDirection: "desc",
        filters,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.page_number < lastPage.total_pages
        ? lastPage.page_number + 1
        : undefined,
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useGenerateWorkerDropdownOptions = (workers?: WorkerType[]) =>
  useMemo(
    () =>
      (workers ?? []).map((w) => ({
        label: w.name,
        value: w.id,
      })),
    [workers]
  );
